import React, { useEffect, useRef, useCallback, useState } from "react";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { baseParametersTC } from "../../utils/tableau/TableauEmbed";
import { useDashboardFiltersContext } from "../../utils/context/dashboardFilters/dashboardFiltersContextStateConfig";
import CustomViewsHandler from "../CustomViewsHandler/CustomViewsHandler";
import {
  applyExtraFilters,
  handleFilterChange,
  handleParameterChange,
} from "../../utils/actions/actions";
import gifLoading from "../../assets/gifs/Lightcast_LoadingGif_Sailor_Red.gif";

const loadingMessages = [
  "Applying saved filters...",
  "Updating dashboard parameters...",
  "This may take a few seconds...",
];

const DashboardUsersLogs = ({
  link,
  locale,
  width = 1000,
  height = 800,
  hideToolbar = false,
  customParameters = [...baseParametersTC, { name: ":language", value: locale }],
  companies,
  sidebar,
  setHideBar,
  stickyFiltersEnabled = false,
  solutionConfigurationEnabled = false,
  userId,
  scope,
}) => {
  const { dashboardFilters, setDashboardFilters } = useDashboardFiltersContext();
  const [dashboardContentLoading, setDashboardContentLoading] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

  const dashboardLoaded = useRef(false);
  const vizHostRef = useRef(null);
  const tableauVizRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!dashboardContentLoading) {
      setLoadingMessageIndex(0);
      return;
    }

    const loadingMessageInterval = setInterval(() => {
      setLoadingMessageIndex(
        (currentIndex) => (currentIndex + 1) % loadingMessages.length
      );
    }, 4000);

    return () => clearInterval(loadingMessageInterval);
  }, [dashboardContentLoading]);

  useEffect(() => {
    if (sidebar === "HIDDEN") {
      setHideBar(sidebar);
    } else if (sidebar === "ALWAYS OPEN") {
      setHideBar(sidebar);
    } else if (sidebar === "CLOSED") {
      setHideBar(true);
    } else {
      setHideBar(null);
    }
  }, [sidebar, setHideBar]);

  const syncVizSize = useCallback(() => {
    const container = containerRef.current;
    const tableauViz = tableauVizRef.current;

    if (!container || !tableauViz) return;

    const { width, height } = container.getBoundingClientRect();

    tableauViz.width = `${width - 10}px`;
    tableauViz.height = `${height - 10}px`;

    tableauViz.style.width = `${width - 10}px`;
    tableauViz.style.height = `${height - 10}px`;
    tableauViz.style.display = "block";
  }, []);

  const initializeViz = useCallback(
    (token, mantainFilters, customView = null) => {
      const host = vizHostRef.current;
      if (!host) {
        setDashboardContentLoading(false);
        return;
      }

      host.innerHTML = "";

      const tableauViz = document.createElement("tableau-viz");
      tableauViz.setAttribute("id", "tableauViz");

      if (hideToolbar) {
        tableauViz.setAttribute("toolbar", "hidden");
        tableauViz.setAttribute("hide-tabs", "");
        tableauViz.setAttribute("hide-edit-button", "");
        tableauViz.setAttribute("hide-edit-in-desktop-button", "");
      }

      tableauViz.setAttribute("token", token);
      tableauViz.src = link;
      tableauViz.language = locale;


      [...customParameters, { name: "ORGANIZATION", value: companies },{name: "VERSION", value: "PUBLIC"}].forEach((param) => {
        const customParam = document.createElement("custom-parameter");
        customParam.setAttribute("name", param.name);
        customParam.setAttribute("value", param.value);
        tableauViz.appendChild(customParam);
      });

      tableauViz.style.width = "100%";
      tableauViz.style.height = "100%";
      tableauViz.style.display = "block";

      tableauVizRef.current = tableauViz;

      if (stickyFiltersEnabled) {
        tableauViz.addEventListener("parameterchanged", (event) =>
          handleParameterChange(event, scope, setDashboardFilters)
        );

        tableauViz.addEventListener("filterchanged", (event) =>
          handleFilterChange(event, scope, setDashboardFilters)
        );

        const filtersToApply = customView ?? dashboardFilters[scope];
        const shouldApplyFilters = customView || (
          (filtersToApply?.filters.length > 0 ||
            filtersToApply?.parameters.length > 0) &&
          mantainFilters
        );

        if (shouldApplyFilters) {
          setDashboardContentLoading(true);
          tableauViz.addEventListener("firstinteractive", async () => {
            await applyExtraFilters(
              filtersToApply,
              scope,
              setDashboardFilters,
              false,
              setDashboardContentLoading
            );
          });
        }
      }

      host.appendChild(tableauViz);
      dashboardLoaded.current = true;

      requestAnimationFrame(() => {
        syncVizSize();
      });
    },
    [
      companies,
      customParameters,
      dashboardFilters,
      hideToolbar,
      link,
      locale,
      scope,
      setDashboardFilters,
      stickyFiltersEnabled,
      syncVizSize,
    ]
  );

  const loadDashboard = useCallback(
    (mantainFilters = true, customView = null) => {
      fetch(
        "https://svi43pyq2a26gzdihakztev72m0isxob.lambda-url.eu-west-1.on.aws/?app=gsw",
        { method: "GET" }
      )
        .then((response) => response.text())
        .then((token) => {
          initializeViz(token, mantainFilters, customView);
        })
        .catch((error) => {
          console.error(error);
          setDashboardContentLoading(false);
        });
    },
    [initializeViz]
  );

  useEffect(() => {
    dashboardLoaded.current = false;
  }, [link]);

  useEffect(() => {
    if (!dashboardLoaded.current) {
      loadDashboard();
    }
  }, [loadDashboard]);

  useEffect(() => {
    if (!containerRef.current) return;

    let resizeTimeoutId = null;

    const observer = new ResizeObserver(() => {
      if (resizeTimeoutId) clearTimeout(resizeTimeoutId);

      resizeTimeoutId = window.setTimeout(() => {
        syncVizSize();
      }, 80);
    });

    observer.observe(containerRef.current);

    return () => {
      if (resizeTimeoutId) clearTimeout(resizeTimeoutId);
      observer.disconnect();
    };
  }, [syncVizSize]);

  return (
    <Row className="justify-content-md-center p-0" id="view">
      <Col
        xs={12}
        className="d-none d-sm-block p-0"
        ref={containerRef}
        id="viz-container"
      >
        {solutionConfigurationEnabled && scope && (
          <CustomViewsHandler
            currentFilters={dashboardFilters[scope] ?? []}
            setFilters={setDashboardFilters}
            loadingApplyFilters={setDashboardContentLoading}
            reloadView={loadDashboard}
            userId={userId}
            scope={scope}
          />
        )}

        <section className="tableau-cloud-stage Users-Logs p-0">
          <div
            ref={vizHostRef}
            className="tableauCloud p-0"
            id="viz"
          />
          {dashboardContentLoading && (
            <div
              className="dashboard-content-loading"
              role="status"
              aria-label="Loading dashboard content"
            >
              <img
                className="loading-screen__gif"
                src={gifLoading}
                alt=""
              />
              <p className="dashboard-content-loading__message pt-4">
                {loadingMessages[loadingMessageIndex]}
              </p>
            </div>
          )}
        </section>
      </Col>
    </Row>
  );
};

export default DashboardUsersLogs;
